import { createSlice } from '@reduxjs/toolkit';

import { fetchSavingsByCategory } from '../services/fetchSavingsByCategory';
import { fetchSavingsOvertime } from '../services/fetchSavingsOvertime';
import {
    fetchSavingsMissedProducts,
    fetchSavingsProducts,
} from '../services/fetchSavingsProducts';
import { fetchSavingsSummary } from '../services/fetchSavingsSummary';

import type {
    ReportSavingsSummarySchema,
    SavingsInsightsLastRequestParams,
} from '../types/ReportSavingsSummarySchema';

const initialState: ReportSavingsSummarySchema = {
    summary: null,
    summaryLoading: false,
    summaryError: null,
    products: null,
    productsLoading: false,
    productsError: null,
    missedProducts: null,
    missedProductsLoading: false,
    missedProductsError: null,
    byCategory: [],
    byCategoryLoading: false,
    byCategoryError: null,
    overtime: [],
    overtimeLoading: false,
    overtimeError: null,
    lastRequestParams: null,
};

export const reportSavingsSummarySlice = createSlice({
    name: 'reportSavingsSummary',
    initialState,
    reducers: {
        setSavingsInsightsLastRequestParams: (
            state,
            action: { payload: SavingsInsightsLastRequestParams },
        ) => {
            state.lastRequestParams = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSavingsSummary.pending, (state) => {
                state.summaryLoading = true;
                state.summaryError = null;
            })
            .addCase(fetchSavingsSummary.fulfilled, (state, action) => {
                state.summary = action.payload;
                state.summaryLoading = false;
                state.summaryError = null;
            })
            .addCase(fetchSavingsSummary.rejected, (state, action) => {
                state.summaryLoading = false;
                state.summaryError = action.payload ?? 'error';
            })
            .addCase(fetchSavingsProducts.pending, (state) => {
                state.productsLoading = true;
                state.productsError = null;
            })
            .addCase(fetchSavingsProducts.fulfilled, (state, action) => {
                state.products = action.payload;
                state.productsLoading = false;
                state.productsError = null;
            })
            .addCase(fetchSavingsProducts.rejected, (state, action) => {
                state.productsLoading = false;
                state.productsError = action.payload ?? 'error';
            })
            .addCase(fetchSavingsMissedProducts.pending, (state) => {
                state.missedProductsLoading = true;
                state.missedProductsError = null;
            })
            .addCase(fetchSavingsMissedProducts.fulfilled, (state, action) => {
                state.missedProducts = action.payload;
                state.missedProductsLoading = false;
                state.missedProductsError = null;
            })
            .addCase(fetchSavingsMissedProducts.rejected, (state, action) => {
                state.missedProductsLoading = false;
                state.missedProductsError = action.payload ?? 'error';
            })
            .addCase(fetchSavingsByCategory.pending, (state) => {
                state.byCategoryLoading = true;
                state.byCategoryError = null;
            })
            .addCase(fetchSavingsByCategory.fulfilled, (state, action) => {
                state.byCategory = action.payload;
                state.byCategoryLoading = false;
                state.byCategoryError = null;
            })
            .addCase(fetchSavingsByCategory.rejected, (state, action) => {
                state.byCategoryLoading = false;
                state.byCategoryError = action.payload ?? 'error';
            })
            .addCase(fetchSavingsOvertime.pending, (state) => {
                state.overtimeLoading = true;
                state.overtimeError = null;
            })
            .addCase(fetchSavingsOvertime.fulfilled, (state, action) => {
                state.overtime = action.payload;
                state.overtimeLoading = false;
                state.overtimeError = null;
            })
            .addCase(fetchSavingsOvertime.rejected, (state, action) => {
                state.overtimeLoading = false;
                state.overtimeError = action.payload ?? 'error';
            });
    },
});

export const { reducer: reportSavingsSummaryReducer } =
    reportSavingsSummarySlice;

export const { setSavingsInsightsLastRequestParams } =
    reportSavingsSummarySlice.actions;
